import { PAYWALL_CLICK_EVENT } from "./paywall";
import { subxUtils } from "./subx_utils";
import { ZEPHR_DECISIONS_FINISHED_EVENT } from "./zephr";

export type SubxUserState = "anonymous" | "registered" | "subscriber";

export type SubxTrackingEvent = {
  brandId: string;
  wId_2: string[];
  dtId: string;
  action: number;
  refer: string;
  requestUrl: string;
  content: {
    increaseRA: boolean;
    articleId: string;
  };
  user: {
    userId: string | undefined;
    userState: SubxUserState;
  };
  context: {
    countryCode?: string;
    userAgent?: string;
    referSource?: string;
    custom?: any;
  };
  product?: {
    offerName?: string;
    offerKey?: string;
  };
};

export type SubxTrackingEventData = {
  action: number;
  increaseRA?: boolean;
  offerId?: string;
  offerName?: string;
};

/**
 * SubX events
 * Page load -> 21
 * Paywall impression -> 44
 * Paywall button click -> 28
 *
 * @see https://aipaywallsubx.notion.site/Client-side-campaign-events-67ca962d6aaa4cfaa1faf1e92af70cd0
 */
export const SUBX_PAGE_LOAD_EVENT = 21;
export const SUBX_PAYWALL_IMPRESSION_EVENT = 44;
export const SUBX_PAYWALL_CLICK_EVENT = 28;

/**
 * Internally used events, not meant to be used in SubX request payloads.
 */
export const SUBX_PAYWALL_VISIBLE_EVENT = "paywall_visible";

export class Subx extends HTMLElement {
  static utils = subxUtils;
  static zephrDecisionsFinished = false;
  static #zephrListenerBound = false;
  static #zephrListener = () => {
    Subx.zephrDecisionsFinished = true;
  };
  private pageLoadEventSent = false;

  get contentId(): string {
    return this.getAttribute("contentid") ?? "";
  }

  get isDebugMode(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("subx") === "true" || urlParams.get("subx") === "1";
  }

  get isEnabled() {
    return this.hasAttribute("enabled");
  }

  get pageIsPaid() {
    return this.hasAttribute("pageispaid");
  }

  get scriptUrl() {
    return this.getAttribute("src") ?? "";
  }

  get vendorId(): string {
    return this.getAttribute("vendorid") ?? "";
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });

    if (!Subx.#zephrListenerBound) {
      document.addEventListener(
        ZEPHR_DECISIONS_FINISHED_EVENT,
        Subx.#zephrListener,
      );
      Subx.#zephrListenerBound = true;
    }
  }

  async connectedCallback() {
    if (this.isEnabled || this.isDebugMode) {
      const consent = await this.#getConsent();
      if (consent) {
        this.bindZephrEvents();
      }

      const checkForTcfApiEvents = () => {
        if (typeof window.__tcfapi === "function") {
          const sourcepoint = document.querySelector("ws-sourcepoint");

          sourcepoint?.tcfApiEventlistener(
            this.#updateConsent.bind(this),
            this.#updateConsent.bind(this),
          );

          clearInterval(intervalId);
        }
      };

      const intervalId = setInterval(checkForTcfApiEvents, 200);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(
      ZEPHR_DECISIONS_FINISHED_EVENT,
      this.#handlePaywallClicks,
    );

    if (Subx.#zephrListenerBound) {
      document.removeEventListener(
        ZEPHR_DECISIONS_FINISHED_EVENT,
        Subx.#zephrListener,
      );
      Subx.#zephrListenerBound = false;
    }
  }

  protected bindZephrEvents() {
    const handleDecisionsFinished = () => {
      this.executeScripts();

      document.removeEventListener(
        ZEPHR_DECISIONS_FINISHED_EVENT,
        handleDecisionsFinished,
      );
    };

    if (Subx.zephrDecisionsFinished) {
      this.executeScripts();
    } else {
      document.addEventListener(
        ZEPHR_DECISIONS_FINISHED_EVENT,
        handleDecisionsFinished,
      );
    }
  }

  protected executeScripts() {
    const script = document.createElement("script");
    script.src = this.scriptUrl;
    script.async = true;

    script.onload = () => {
      document.addEventListener(PAYWALL_CLICK_EVENT, this.#handlePaywallClicks);
      document.addEventListener(
        SUBX_PAYWALL_VISIBLE_EVENT,
        this.#onPaywallVisible,
      );
      if (!this.pageLoadEventSent) {
        this.sendSubxTrackingEvent({
          action: SUBX_PAGE_LOAD_EVENT,
          increaseRA: !this.pageIsPaid,
        });
        this.pageLoadEventSent = true;
      }
    };

    script.onerror = function (e) {
      console.log(JSON.stringify(e));
    };

    this.shadowRoot!.appendChild(script);
  }

  async #getConsent(): Promise<boolean> {
    const sourcepoint = document.querySelector("ws-sourcepoint");

    if (!sourcepoint) return false;

    await window.customElements.whenDefined("ws-sourcepoint");

    await sourcepoint.waitForTcfApi();

    let hasConsent = false;

    try {
      const data = await new Promise<any>((resolve, reject) => {
        try {
          window.__tcfapi("getCustomVendorConsents", 2, (result: any) => {
            resolve(result);
          });
        } catch (err) {
          reject(err);
        }
      });

      hasConsent = data?.consentedVendors?.some(
        (vendor: any) => vendor._id === this.vendorId,
      );
    } catch (err) {
      console.error("Error getting consent: ", err);
      return false;
    }

    return hasConsent && !sourcepoint.isPURSubscriber;
  }

  async #updateConsent() {
    const consent = await this.#getConsent();
    if (consent) {
      this.bindZephrEvents();
    }
  }

  #handlePaywallClicks = () => {
    this.sendSubxTrackingEvent({
      action: SUBX_PAYWALL_CLICK_EVENT,
      offerId: Subx.utils.getOfferId(),
      offerName: Subx.utils.getOfferName(),
    });
  };

  #onPaywallVisible = () => {
    this.sendSubxTrackingEvent({
      action: SUBX_PAYWALL_IMPRESSION_EVENT,
      offerId: Subx.utils.getOfferId(),
      offerName: Subx.utils.getOfferName(),
    });
  };

  #sendEvent(trackingEvent: SubxTrackingEvent) {
    if (typeof window.ZITFunctions.sendData === "function") {
      window.ZITFunctions.sendData(trackingEvent);
    } else {
      console.error("ZITFunctions.sendData is not defined.");
    }
  }

  async sendSubxTrackingEvent(eventData: SubxTrackingEventData) {
    const {
      action,
      increaseRA = true,
      offerId = "",
      offerName = "",
    } = eventData;
    const referSource = Subx.utils.getOriginalReferrer();

    const data: SubxTrackingEvent = {
      brandId: window.ZITFunctions?.brandId,
      wId_2: [offerId],
      dtId: window.ZITFunctions?.retrieveOrigSession(),
      action,
      refer: document.referrer,
      requestUrl: window.location.href,
      content: {
        increaseRA,
        articleId: this.contentId,
      },
      user: {
        userId: await Subx.utils.getUserId(),
        userState: window.ZITFunctions?.retrieveUserStatus(),
      },
      context: {
        countryCode: "DEU",
        userAgent: navigator.userAgent,
        ...(referSource && { referSource }),
      },
      ...(offerId && {
        product: {
          offerName,
          offerKey: offerId,
        },
      }),
    };

    this.#sendEvent(data);
  }
}

customElements.get("ws-subx") ?? customElements.define("ws-subx", Subx);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-subx": Subx;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    ZITFunctions: {
      brandId: string;
      retrieveOrigSession: () => string;
      retrieveUserStatus: () => SubxUserState;
      sendData: (data: SubxTrackingEvent) => void;
    };
  }
}
